import { persistentMap } from '@nanostores/persistent';

import type { HintId, HintProgressEntry } from './types';

// `:v1` suffix lets us migrate to `:v2:` later without colliding with old data.
const STORAGE_PREFIX = 'hint:progress:v1:';

type ProgressMap = Record<HintId, HintProgressEntry>;

/** Persisted dismissals. Local-only for now; server reconciliation is a follow-up. */
export const $hintProgress = persistentMap<ProgressMap>(
  STORAGE_PREFIX,
  {},
  {
    encode: (value) => JSON.stringify(value),
    decode: decodeHintProgressEntry,
  }
);

/**
 * Returns `undefined` for malformed or wrong-shape entries.
 *
 * Cast routes around `PersistentEncoder<Origin>`'s strict return type — the
 * map consumer (`registry.isEligible`) already treats `undefined` entries
 * as "never seen", so this is the intended semantics.
 */
function decodeHintProgressEntry(raw: string): HintProgressEntry {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isHintProgressEntry(parsed)) {
      return parsed;
    }
  } catch {
    /* Fall through here, since we don't want invalid entries to crash the app.*/
  }
  return undefined as unknown as HintProgressEntry;
}

function isHintProgressEntry(value: unknown): value is HintProgressEntry {
  return (
    value !== null &&
    typeof value === 'object' &&
    'version' in value &&
    typeof (value as { version: unknown }).version === 'number' &&
    'at' in value &&
    typeof (value as { at: unknown }).at === 'number'
  );
}

export function getHintProgress(id: HintId): HintProgressEntry | null {
  return $hintProgress.get()[id] ?? null;
}

/** Record a dismissal at `version`. Bump the hint's version to re-show. */
export function dismissHint(id: HintId, version: number): void {
  $hintProgress.setKey(id, { version, at: Date.now() });
}

/**
 * Reset to "never seen". `set(newObject)` rather than `setKey(_, undefined)`
 * because the branded `HintId` key type rejects `undefined`.
 */
export function clearHint(id: HintId): void {
  const current = $hintProgress.get();
  if (!(id in current)) {
    return;
  }
  const next: ProgressMap = { ...current };
  delete next[id];
  $hintProgress.set(next);
}

/** Reset every hint to "never seen", removing each persisted entry. */
export function clearAllHints(): void {
  $hintProgress.set({});
}
